import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { fetchAiConsent, grantAiConsent } from '@/lib/api';
import { AI_DISCLOSURE_VERSION, consentProvider, hasAiGrant } from '@/lib/aiConsent.mjs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

export default function useAiSharing(siteId, siteName, model, isDemo) {
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const resolve = useRef(null);
  const context = useRef('');
  context.current = `${siteId}:${model}`;
  useEffect(() => { resolve.current?.(false); resolve.current = null; setPending(null); }, [siteId, model]);
  useEffect(() => () => { resolve.current?.(false); }, []);
  const finish = value => { resolve.current?.(value); resolve.current = null; setPending(null); };
  const request = async () => {
    if (isDemo) return true;
    const provider = consentProvider(model);
    if (!provider) { toast.error('OpenRouter sharing is unavailable: the serving recipient has not been verified. Choose Anthropic or OpenAI.'); return false; }
    const requestedContext = context.current;
    try {
      const record = await fetchAiConsent(siteId);
      if (requestedContext !== context.current) return false;
      if (hasAiGrant(record, provider)) return true;
      return await new Promise(done => { resolve.current?.(false); resolve.current = done; setPending({provider, siteId, context: requestedContext}); });
    } catch { toast.error('AI sharing permission could not be checked. Your reports remain available.'); return false; }
  };
  const allow = async () => {
    setBusy(true);
    try {
      await grantAiConsent(pending.siteId, pending.provider, AI_DISCLOSURE_VERSION);
      const record = await fetchAiConsent(pending.siteId);
      if (!hasAiGrant(record, pending.provider)) throw new Error("Grant was not confirmed");
      if (pending.context === context.current) finish(true);
    } catch { toast.error('Permission could not be saved. No AI request has been sent.'); }
    finally { setBusy(false); }
  };
  const recipient = pending?.provider === 'anthropic' ? 'Anthropic' : 'OpenAI';
  const dialog = <Dialog open={Boolean(pending)} onOpenChange={open => { if (!open && !busy) finish(false); }}><DialogContent><DialogHeader><DialogTitle>Allow AI explanations for {siteName || siteId} using {recipient}?</DialogTitle><DialogDescription>SEO Signals will send your question, relevant conversation context and a summary of this site's connected Search Console and Analytics reports to {recipient} to generate an answer. Connected Bing and Clarity summaries may also be included. Your Google password and access tokens are not sent. This permission applies to AI explanations and generated insights for this site using {recipient}. You can withdraw it in Settings. Your data reports remain available without AI sharing. <a href="/privacy">Privacy policy</a>.</DialogDescription></DialogHeader><button data-testid="ai-sharing-allow" disabled={busy} onClick={allow} className="rounded-md bg-emerald-700 px-4 py-3 text-white">{busy ? 'Saving permission…' : 'Allow sharing and generate'}</button><button data-testid="ai-sharing-decline" disabled={busy} onClick={() => finish(false)}>Continue with reports</button></DialogContent></Dialog>;
  return { request, dialog };
}
